import AdminGuard from "helpers/AuthType/AdminGuard";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import {
    Card,
    Col,
    Container,
    Row,
    CardBody,
    CardTitle,
    Label,
    Input,
    Button,
    Table,
    Badge
} from "reactstrap";
import { createPackageApi, getAllPackagesApi, getBuilderDropdownApi } from "service/api";

const PackageForm = () => {
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);

    const [builderOptions, setBuilderOptions] = useState([]);
    const [formData, setFormData] = useState({
        package_name: "",
        builder_id: null,
        start_date: "",
        end_date: "",
        total_allowed: "",
        used: 0,
        remaining: 0,
        status: "Upcoming"
    });

    // ======================
    // LOAD BUILDERS
    // ======================
    useEffect(() => {
        const loadBuilders = async () => {
            try {
                const res = await getBuilderDropdownApi();

                console.log("BUILDER RESPONSE:", res);

                if (!res.success || !Array.isArray(res.data)) {
                    toast.error("Builder data format error");
                    return;
                }

                const options = res.data.map(b => ({
                    value: Number(b.id),
                    label: String(b.dev_name).trim()
                }));

                setBuilderOptions(options);
            } catch (err) {
                console.error("Builder API Error:", err);
                toast.error("Failed to load builders");
            }
        };

        loadBuilders();
        loadPackages();

    }, []);
    const loadPackages = async () => {
        try {
            const res = await getAllPackagesApi();
            if (res.success) {
                setPackages(res.data);
            }
        } catch {
            toast.error("Failed to load packages");
        }
    };

    // ======================
    // INPUT HANDLER
    // ======================
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            if (name === "total_allowed") {
                const total = parseInt(value || 0);
                updated.remaining = Math.max(total - prev.used, 0);
            }

            return updated;
        });
    };

    const handleSelect = (option) => {
        setFormData(prev => ({
            ...prev,
            builder_id: option
        }));
    };

    // ======================
    // AUTO STATUS LOGIC
    // ======================
    useEffect(() => {
        if (!formData.start_date || !formData.end_date) return;

        const today = new Date();
        const start = new Date(formData.start_date);
        const end = new Date(formData.end_date);

        let status = "Upcoming";

        if (today >= start && today <= end) status = "Active";
        else if (today > end) status = "Expired";

        setFormData(prev => ({ ...prev, status }));
    }, [formData.start_date, formData.end_date]);

    // ======================
    // VALIDATION
    // ======================
    const validateForm = () => {
        return (
            formData.package_name &&
            formData.builder_id &&
            formData.start_date &&
            formData.end_date &&
            formData.total_allowed
        );
    };

    // ======================
    // SUBMIT
    // ======================
    const AddPackage = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fill all mandatory fields");
            return;
        }

        const payload = {
            package_name: formData.package_name,
            builder_id: formData.builder_id.value,
            start_date: formData.start_date,
            end_date: formData.end_date,
            total_allowed: parseInt(formData.total_allowed),
            used: formData.used,
            remaining: formData.remaining,
            status: formData.status
        };

        try {
            await createPackageApi(payload);
            toast.success("Package saved successfully!");
            // navigate(-1);
            loadPackages();

        } catch {
            toast.error("Failed to save package");
        }
    };

    // ======================
    // SELECT STYLES
    // ======================
    const selectStyle = {
        control: (base) => ({
            ...base,
            background: "rgba(255,255,255,0.15)",
            borderColor: "rgba(255,255,255,0.3)",
            color: "#fff"
        }),
        singleValue: (base) => ({ ...base, color: "#fff" }),
        menu: (base) => ({ ...base, background: "#1E253A" }),
        option: (base, state) => ({
            ...base,
            background: state.isFocused ? "#6f42c1" : "#1E253A",
            color: "#fff",
            cursor: "pointer"
        })
    };

    return (
        <AdminGuard>
            <React.Fragment>
                <ToastContainer />
                <div className="page-content" style={{ color: "#fff" }}>
                    <Container fluid className="py-2">
                        <Row className="justify-content-center">
                            <Col xl={12}>
                                <Card
                                    style={{
                                        background: "rgba(30,37,58,0.95)",
                                        borderRadius: "16px",
                                        border: "1px solid rgba(255,255,255,0.2)"
                                    }}
                                >
                                    <CardBody>
                                        {/* HEADER */}
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <CardTitle className="h3 mb-0">
                                                <i className="bx bx-package me-2" />
                                                Create Package
                                            </CardTitle>

                                            <Button
                                                size="sm"
                                                onClick={() => navigate(-1)}
                                                style={{
                                                    background: "rgba(255,255,255,0.1)",
                                                    border: "1px solid rgba(255,255,255,0.3)",
                                                    borderRadius: "10px",
                                                    color: "#fff",
                                                    padding: "6px 14px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}
                                            >
                                                <i className="bx bx-arrow-back" />
                                                Back
                                            </Button>
                                        </div>

                                        <form>
                                            <Row>
                                                {/* PACKAGE NAME */}
                                                <Col md={4}>
                                                    <Label>Package Name *</Label>
                                                    <Input
                                                        name="package_name"
                                                        placeholder="e.g. January Builder Plan"
                                                        onChange={handleChange}
                                                        style={{
                                                            background: "rgba(255,255,255,0.15)",
                                                            color: "#fff",
                                                            border: "1px solid rgba(255,255,255,0.2)"
                                                        }}
                                                    />
                                                </Col>

                                                {/* BUILDER */}
                                                <Col md={4}>
                                                    <Label>Builder *</Label>
                                                    <Select
                                                        options={builderOptions}
                                                        value={formData.builder_id}
                                                        styles={selectStyle}
                                                        onChange={handleSelect}
                                                        placeholder="Select Builder"
                                                        getOptionLabel={(e) => e.label}
                                                        getOptionValue={(e) => e.value.toString()}
                                                    />

                                                </Col>
                                            </Row>

                                            <Row className="mt-3">
                                                <Col md={3}>
                                                    <Label>Start Date *</Label>
                                                    <Input
                                                        type="date"
                                                        name="start_date"
                                                        onChange={handleChange}
                                                        style={{
                                                            background: "rgba(255,255,255,0.15)",
                                                            color: "#fff",
                                                            border: "1px solid rgba(255,255,255,0.2)"
                                                        }}
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Label>End Date *</Label>
                                                    <Input
                                                        type="date"
                                                        name="end_date"
                                                        onChange={handleChange}
                                                        style={{
                                                            background: "rgba(255,255,255,0.15)",
                                                            color: "#fff",
                                                            border: "1px solid rgba(255,255,255,0.2)"
                                                        }}
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Label>Total Walkings *</Label>
                                                    <Input
                                                        type="number"
                                                        name="total_allowed"
                                                        placeholder="e.g. 50"
                                                        min="0"
                                                        onChange={handleChange}
                                                        style={{
                                                            background: "rgba(255,255,255,0.15)",
                                                            color: "#fff",
                                                            border: "1px solid rgba(255,255,255,0.2)"
                                                        }}
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mt-3">
                                                <Col md={3}>
                                                    <Label>Used</Label>
                                                    <Input
                                                        disabled
                                                        value={formData.used}
                                                        style={{
                                                            background: "rgba(255,255,255,0.05)",
                                                            color: "#aaa",
                                                            border: "1px solid rgba(255,255,255,0.1)"
                                                        }}
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Label>Remaining</Label>
                                                    <Input
                                                        disabled
                                                        value={formData.remaining}
                                                        style={{
                                                            background: "rgba(255,255,255,0.05)",
                                                            color: "#aaa",
                                                            border: "1px solid rgba(255,255,255,0.1)"
                                                        }}
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Label>Status</Label>
                                                    <Input
                                                        disabled
                                                        value={formData.status}
                                                        style={{
                                                            background: "rgba(255,255,255,0.05)",
                                                            color:
                                                                formData.status === "Active"
                                                                    ? "#28a745"
                                                                    : formData.status === "Expired"
                                                                        ? "#dc3545"
                                                                        : "#ffc107",
                                                            border: "1px solid rgba(255,255,255,0.1)",
                                                            fontWeight: "bold"
                                                        }}
                                                    />
                                                </Col>
                                            </Row>

                                            <div className="d-flex justify-content-end mt-4">
                                                <Button
                                                    onClick={AddPackage}
                                                    style={{
                                                        background: "#6f42c1",
                                                        border: "none",
                                                        borderRadius: "12px",
                                                        padding: "10px 30px",
                                                        fontSize: "16px"
                                                    }}
                                                >
                                                    <i className="bx bx-save me-1" />
                                                    Save Package
                                                </Button>
                                            </div>
                                        </form>
                                    </CardBody>
                                </Card>

                            </Col>
                        </Row>
                        {/* ================= PACKAGE LIST ================= */}
                        <Row className="mt-4">
                            <Col xl={12}>
                                <Card
                                    style={{
                                        background: "rgba(30,37,58,0.95)",
                                        borderRadius: "16px",
                                        border: "1px solid rgba(255,255,255,0.2)"
                                    }}
                                >
                                    <CardBody>
                                        <CardTitle className="h4 mb-3">
                                            <i className="bx bx-list-ul me-2" />
                                            Package List
                                        </CardTitle>

                                        <Table dark bordered responsive hover>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Package</th>
                                                    <th>Builder</th>
                                                    <th>Start</th>
                                                    <th>End</th>
                                                    <th>Total</th>
                                                    <th>Used</th>
                                                    <th>Remaining</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {packages.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center">
                                                            No packages found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    packages.map((pkg, i) => (
                                                        <tr key={pkg.id}>
                                                            <td>{i + 1}</td>
                                                            <td>{pkg.package_name}</td>
                                                            <td>{pkg.builder_name}</td>
                                                            <td>{pkg.start_date}</td>
                                                            <td>{pkg.end_date}</td>
                                                            <td>{pkg.total_allowed}</td>
                                                            <td>{pkg.used}</td>
                                                            <td>{pkg.remaining}</td>
                                                            <td>
                                                                <Badge
                                                                    color={
                                                                        pkg.status === "Active"
                                                                            ? "success"
                                                                            : pkg.status === "Expired"
                                                                                ? "danger"
                                                                                : "warning"
                                                                    }
                                                                >
                                                                    {pkg.status}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                    </Container>
                </div>
            </React.Fragment>
        </AdminGuard>
    );
};

export default PackageForm;
